import React from "react";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Search, Activity, ChevronRight, Building2, ShieldCheck, TrendingUp } from "lucide-react";
import { useState } from "react";

const roleTone = {
  owner: "bg-[#C9A24B]/15 text-[#C9A24B]",
  manager: "bg-sky-500/15 text-sky-300",
  telecaller: "bg-emerald-500/15 text-emerald-300",
};

function initials(name) {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function NameSwitcher() {
  const { activeEmployees, selectUser } = useApp();
  const [q, setQ] = useState("");

  const filtered = activeEmployees.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#0b1120]">
      {/* left hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img alt="Clinic" className="absolute inset-0 w-full h-full object-cover"
          src="https://images.pexels.com/photos/16571735/pexels-photo-16571735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200" />
        <div className="absolute inset-0 bg-[#0b1120]/80" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="text-[#C9A24B] font-heading text-xl tracking-wide">AuraMed Centre</div>
          <div>
            <h1 className="font-heading text-4xl font-light leading-tight text-white">Sales & Telecalling<br />Intelligence CRM</h1>
            <p className="text-slate-300 mt-4 max-w-md">Track every call, visit and treatment. Give your team the invisible hand that converts.</p>
            <div className="flex gap-6 mt-8 text-sm text-slate-300">
              <span className="flex items-center gap-2"><ShieldCheck className="h-[18px] w-[18px] text-emerald-400" /> Auto scoring</span>
              <span className="flex items-center gap-2"><TrendingUp className="h-[18px] w-[18px] text-[#C9A24B]" /> Live goal tracking</span>
            </div>
          </div>
        </div>
      </div>

      {/* right select panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-2xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-lg bg-[#C9A24B] flex items-center justify-center text-[#0b1120]"><Activity className="h-6 w-6" /></div>
            <div>
              <div className="font-heading text-lg text-white">AuraMed OT CRM</div>
              <div className="text-xs text-slate-500">Select your name to continue</div>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-light mb-1 text-white">Welcome back</h2>
          <p className="text-slate-400 text-sm mb-6">No password needed — just pick your name to start tracking.</p>

          <div className="relative mb-5">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input data-testid="name-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search team member…" className="pl-10 h-12 rounded-xl" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[52vh] overflow-y-auto scrollbar-thin pr-1">
            {filtered.map((emp, i) => (
              <button key={emp.id} data-testid={`name-card-${emp.id}`} onClick={() => selectUser(emp)}
                className="group text-left surface p-4 transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-[#C9A24B]/40 animate-fade-up"
                style={{ animationDelay: `${i * 35}ms` }}>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#C9A24B] to-[#a07d33] text-[#0b1120] flex items-center justify-center font-heading font-bold shadow-md">{initials(emp.name)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-white truncate">{emp.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${roleTone[emp.role] || "bg-white/10 text-slate-300"}`}>{emp.role}</span>
                      {emp.team && <span className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> {emp.team}</span>}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-600 group-hover:text-[#C9A24B] group-hover:translate-x-0.5 transition-[color,transform]" />
                </div>
              </button>
            ))}
          </div>
          {filtered.length === 0 && <div className="text-center text-slate-500 py-10">No team member found.</div>}
        </div>
      </div>
    </div>
  );
}
